import React, { useState, useEffect } from 'react';
import { BarChart3, DollarSign, CheckCircle, Clock, XCircle, RefreshCw, FileText, ClipboardList, Handshake, Calendar, Upload as UploadIcon, Download } from 'lucide-react';
import { useLanguage } from './LanguageContext';

interface PartnerViewProps {
  partnerType: 'cooperation' | 'referral';
  currentInvestor: any;
  vipStatus: string | null;
  apiUrl: string;
  language: string;
}

const PartnerView: React.FC<PartnerViewProps> = ({ partnerType, currentInvestor, vipStatus, apiUrl, language }) => {
  const isDE = language === 'de';

  if (partnerType === 'referral') {
    return <ReferralPartnerView isDE={isDE} />;
  }

  return (
    <CooperationPartnerView
      currentInvestor={currentInvestor}
      vipStatus={vipStatus}
      apiUrl={apiUrl}
      isDE={isDE}
    />
  );
};

/* ================================================================
   COOPERATION PARTNER VIEW — 3 Tabs
   ================================================================ */
interface CoopProps {
  currentInvestor: any;
  vipStatus: string | null;
  apiUrl: string;
  isDE: boolean;
}

const CooperationPartnerView: React.FC<CoopProps> = ({ currentInvestor, vipStatus, apiUrl, isDE }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'application' | 'dashboard' | 'commissions'>('application');
  const [isCooperationPartner, setIsCooperationPartner] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState({
    date: '',
    salutation: '',
    firstName: '',
    lastName: '',
    companyName: '',
    streetAddress: '',
    postalCode: '',
    city: '',
    country: '',
    email: '',
    phone: '',
    reason: '',
    experience: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [dashboardData, setDashboardData] = useState<any>(null);
  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');
  const [commissionSheets, setCommissionSheets] = useState<any[]>([]);

  useEffect(() => {
    checkPartnerStatus();
  }, [currentInvestor]);

  useEffect(() => {
    if (isCooperationPartner) {
      loadDashboard();
      loadCommissionSheets();
    }
  }, [isCooperationPartner, selectedYear]);

  async function checkPartnerStatus() {
    setIsLoading(true);
    try {
      const isPartner = vipStatus === 'VVIP - Cooperation Partner';
      setIsCooperationPartner(isPartner);
      if (currentInvestor?.id) {
        const res = await fetch(`${apiUrl}/api/cooperation/applications/investor/${encodeURIComponent(currentInvestor.id)}`);
        if (res.ok) {
          const data = await res.json();
          setApplicationStatus(data.application || null);
          if (data.application?.status === 'approved') {
            setIsCooperationPartner(true);
          }
        }
      }
      if (currentInvestor) {
        setFormData(prev => ({
          ...prev,
          email: currentInvestor.email || currentInvestor.id || '',
          firstName: currentInvestor.name?.split(' ')[0] || '',
          lastName: currentInvestor.name?.split(' ').slice(1).join(' ') || '',
          phone: currentInvestor.phone || '',
        }));
      }
    } catch (err) {
      console.error('Error checking partner status:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function loadDashboard() {
    try {
      const yearParam = selectedYear === 'all' ? new Date().getFullYear() : selectedYear;
      const res = await fetch(`${apiUrl}/api/cooperation/partner/${encodeURIComponent(currentInvestor.id)}/dashboard?year=${yearParam}`);
      if (res.ok) {
        const data = await res.json();
        setDashboardData(data);
      }
    } catch (err) { console.error('Error loading dashboard:', err); }
  }

  async function loadCommissionSheets() {
    try {
      const res = await fetch(`${apiUrl}/api/cooperation/commission-sheets?partnerId=${encodeURIComponent(currentInvestor.id)}`);
      if (res.ok) {
        const data = await res.json();
        setCommissionSheets(data);
      }
    } catch (err) { console.error('Error loading commissions:', err); }
  }

  async function submitApplication(e: React.FormEvent) {
    e.preventDefault();
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
      setSubmitMessage({ type: 'error', text: isDE ? 'Bitte Pflichtfelder ausfüllen (Vorname, Nachname, E-Mail).' : 'Please fill required fields (First Name, Last Name, Email).' });
      return;
    }
    setIsSubmitting(true);
    setSubmitMessage(null);
    try {
      const investorName = `${formData.salutation ? formData.salutation + ' ' : ''}${formData.firstName} ${formData.lastName}`.trim();
      const res = await fetch(`${apiUrl}/api/cooperation/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          investorId: currentInvestor.id,
          investorName,
          investorEmail: formData.email,
          investorPhone: formData.phone,
          companyName: formData.companyName,
          reason: formData.reason,
          experience: formData.experience,
          applicationDetails: {
            date: formData.date, salutation: formData.salutation,
            firstName: formData.firstName, lastName: formData.lastName,
            streetAddress: formData.streetAddress, postalCode: formData.postalCode,
            city: formData.city, country: formData.country,
          }
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSubmitMessage({ type: 'success', text: isDE ? 'Antrag erfolgreich eingereicht! Wir prüfen ihn in Kürze.' : 'Application submitted successfully! We will review it shortly.' });
        checkPartnerStatus();
      } else {
        setSubmitMessage({ type: 'error', text: data.error || 'Failed to submit' });
      }
    } catch (err: any) {
      setSubmitMessage({ type: 'error', text: err.message || 'Network error' });
    } finally { setIsSubmitting(false); }
  }

  const updateForm = (field: string, value: string) => setFormData(prev => ({ ...prev, [field]: value }));

  const tabs = [
    { id: 'application' as const, label: isDE ? 'Bewerbung' : 'Application', icon: <ClipboardList size={14} /> },
    { id: 'dashboard' as const, label: 'Dashboard', icon: <BarChart3 size={14} /> },
    { id: 'commissions' as const, label: isDE ? 'Provisionen' : 'Commissions', icon: <DollarSign size={14} /> },
  ];

  if (isLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid #eee', borderTopColor: '#D6B160', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '40px auto' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px', maxWidth: '600px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)', borderRadius: '16px', padding: '24px', color: 'white', marginBottom: '20px' }}>
        <div style={{ fontSize: '12px', color: '#D6B160', fontWeight: '600', marginBottom: '4px', letterSpacing: '1px', textTransform: 'uppercase' }}>
          {isDE ? 'Kooperationspartner' : 'Cooperation Partner'}
        </div>
        <h2 style={{ fontSize: '22px', fontWeight: '700', margin: '0 0 6px 0' }}>
          <Handshake size={20} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />{isDE ? 'Partnerschaft' : 'Partnership'}
        </h2>
        <p style={{ fontSize: '13px', opacity: 0.8, margin: 0 }}>
          {isDE ? 'Werden Sie Asset Desk Kooperationspartner' : 'Become an Asset Desk Cooperation Partner'}
        </p>
      </div>

      {/* 3 Tabs */}
      <div style={{ display: 'flex', gap: '6px', background: '#f5f5f5', borderRadius: '12px', padding: '4px', marginBottom: '20px' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            flex: 1, padding: '10px 6px', borderRadius: '10px', border: 'none',
            background: activeTab === tab.id ? '#1a1a1a' : 'transparent',
            color: activeTab === tab.id ? '#D6B160' : '#666',
            fontWeight: '600', fontSize: '12px', cursor: 'pointer', transition: 'all 0.2s',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
          }}>
            <span>{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>

      {/* TAB 1: APPLICATION */}
      {activeTab === 'application' && (
        <div>
          {isCooperationPartner && (
            <div style={{ background: 'linear-gradient(135deg, #065f46, #047857)', borderRadius: '16px', padding: '24px', color: 'white', textAlign: 'center' }}>
              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}><CheckCircle size={48} color="#fff" /></div>
              <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>{isDE ? 'Aktiver Kooperationspartner' : 'Active Cooperation Partner'}</h3>
              <div style={{ display: 'inline-block', padding: '6px 16px', background: 'rgba(255,255,255,0.15)', borderRadius: '20px', fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>{t('ptnVvipCooperationPartner')}</div>
              <p style={{ fontSize: '13px', opacity: 0.9, margin: 0 }}>{isDE ? 'Sie sind bereits als Kooperationspartner registriert.' : 'You are already registered as a Cooperation Partner.'}</p>
            </div>
          )}
          {!isCooperationPartner && applicationStatus?.status === 'pending' && (
            <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' }}>
              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}><Clock size={48} color="#f59e0b" /></div>
              <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>{isDE ? 'Antrag wird geprüft' : 'Application Under Review'}</h3>
              <p style={{ fontSize: '13px', color: '#666', lineHeight: '1.6' }}>{isDE ? 'Ihr Antrag wird derzeit geprüft. Wir melden uns in Kürze.' : 'Your application is being reviewed. We will get back to you shortly.'}</p>
              <div style={{ marginTop: '16px', padding: '12px', background: '#fef3c7', borderRadius: '10px', fontSize: '12px', color: '#92400e' }}>
                <Calendar size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{isDE ? 'Eingereicht am' : 'Submitted on'}: {applicationStatus.appliedAt?._seconds ? new Date(applicationStatus.appliedAt._seconds * 1000).toLocaleDateString() : '-'}
              </div>
            </div>
          )}
          {!isCooperationPartner && applicationStatus?.status === 'rejected' && (
            <div>
              <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center', marginBottom: '16px' }}>
                <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}><XCircle size={48} color="#ef4444" /></div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>{isDE ? 'Antrag nicht genehmigt' : 'Application Not Approved'}</h3>
                {applicationStatus.rejectionReason && <p style={{ fontSize: '13px', color: '#666' }}>{applicationStatus.rejectionReason}</p>}
                <p style={{ fontSize: '12px', color: '#999' }}>{isDE ? 'Sie können sich erneut bewerben.' : 'You may re-apply below.'}</p>
              </div>
              <ApplicationForm formData={formData} updateForm={updateForm} onSubmit={submitApplication} isSubmitting={isSubmitting} submitMessage={submitMessage} isDE={isDE} />
            </div>
          )}
          {!isCooperationPartner && !applicationStatus && (
            <ApplicationForm formData={formData} updateForm={updateForm} onSubmit={submitApplication} isSubmitting={isSubmitting} submitMessage={submitMessage} isDE={isDE} />
          )}
        </div>
      )}

      {/* TAB 2: DASHBOARD */}
      {activeTab === 'dashboard' && (
        <DashboardTab isCooperationPartner={isCooperationPartner} dashboardData={dashboardData} selectedYear={selectedYear} setSelectedYear={setSelectedYear} isDE={isDE} />
      )}

      {/* TAB 3: COMMISSIONS */}
      {activeTab === 'commissions' && (
        <CommissionsTab isCooperationPartner={isCooperationPartner} commissionSheets={commissionSheets} isDE={isDE} apiUrl={apiUrl} />
      )}
    </div>
  );
};

/* ================================================================
   APPLICATION FORM
   ================================================================ */
interface FormProps {
  formData: any;
  updateForm: (field: string, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
  submitMessage: { type: string; text: string } | null;
  isDE: boolean;
}

const ApplicationForm: React.FC<FormProps> = ({ formData, updateForm, onSubmit, isSubmitting, submitMessage, isDE }) => {
  const { t } = useLanguage();
  const inputStyle: React.CSSProperties ={ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' as const, background: 'white' };
  const labelStyle: React.CSSProperties = { display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '6px', color: '#333' };
  const fieldStyle: React.CSSProperties = { marginBottom: '16px' };
  const smallStyle: React.CSSProperties = { fontSize: '11px', color: '#999', marginTop: '4px', display: 'block' };

  return (
    <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px' }}>📝 {isDE ? 'Kooperationspartner Antrag' : 'Cooperation Partner Application'}</h3>
      <p style={{ fontSize: '12px', color: '#666', marginBottom: '20px' }}>{isDE ? 'Füllen Sie das Formular aus, um Kooperationspartner zu werden.' : 'Fill out the form to apply as a Cooperation Partner.'}</p>

      {submitMessage && (
        <div style={{ padding: '12px', borderRadius: '8px', marginBottom: '16px', background: submitMessage.type === 'success' ? '#dcfce7' : '#fee2e2', color: submitMessage.type === 'success' ? '#166534' : '#dc2626', fontSize: '13px', fontWeight: '500' }}>{submitMessage.text}</div>
      )}

      <form onSubmit={onSubmit}>
        <div style={fieldStyle}>
          <label style={labelStyle}>{isDE ? 'Datum' : 'Date'}</label>
          <input type="date" value={formData.date} onChange={e => updateForm('date', e.target.value)} style={inputStyle} placeholder="DD/MM/YYYY" />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{isDE ? 'Anrede' : 'Mr/Ms'}</label>
          <select value={formData.salutation} onChange={e => updateForm('salutation', e.target.value)} style={{ ...inputStyle, color: formData.salutation ? '#333' : '#999' }}>
            <option value="">{isDE ? 'Bitte wählen' : 'Choose value'}</option>
            <option value="Mr">{t('ptnSalutationMr')}</option>
            <option value="Ms">{t('ptnSalutationMs')}</option>
            <option value="Mrs">{t('ptnSalutationMrs')}</option>
            <option value="Dr">{t('ptnSalutationDr')}</option>
          </select>
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{isDE ? 'Vorname' : 'First Name'} *</label>
          <input type="text" value={formData.firstName} onChange={e => updateForm('firstName', e.target.value)} required style={inputStyle} />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{isDE ? 'Nachname' : 'Last Name'} *</label>
          <input type="text" value={formData.lastName} onChange={e => updateForm('lastName', e.target.value)} required style={inputStyle} />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{isDE ? 'Firmenname' : 'Company Name'}</label>
          <input type="text" value={formData.companyName} onChange={e => updateForm('companyName', e.target.value)} style={inputStyle} />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{isDE ? 'Adresse' : 'Address'}</label>
          <input type="text" value={formData.streetAddress} onChange={e => updateForm('streetAddress', e.target.value)} style={inputStyle} />
          <small style={smallStyle}>{isDE ? 'Hausnummer und Straßenname' : 'House No. and Street Name'}</small>
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{isDE ? 'Stadt' : 'City'}</label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input type="text" value={formData.postalCode} onChange={e => updateForm('postalCode', e.target.value)} placeholder={isDE ? 'PLZ' : 'Postal Code'} style={{ ...inputStyle, flex: '0 0 120px' }} />
            <input type="text" value={formData.city} onChange={e => updateForm('city', e.target.value)} placeholder={isDE ? 'Stadt' : 'City'} style={{ ...inputStyle, flex: 1 }} />
          </div>
          <small style={smallStyle}>{isDE ? 'Postleitzahl, Stadt' : 'Postal Code, City'}</small>
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{isDE ? 'Land' : 'Country'}</label>
          <input type="text" value={formData.country} onChange={e => updateForm('country', e.target.value)} style={inputStyle} />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{isDE ? 'E-Mail-Adresse' : 'Email Address'} *</label>
          <input type="email" value={formData.email} onChange={e => updateForm('email', e.target.value)} required style={inputStyle} />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{isDE ? 'Telefonnummer' : 'Phone Number'}</label>
          <input type="tel" value={formData.phone} onChange={e => updateForm('phone', e.target.value)} style={inputStyle} />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{isDE ? 'Warum möchten Sie Kooperationspartner werden?' : 'Why do you want to become a Cooperation Partner?'}</label>
          <textarea value={formData.reason} onChange={e => updateForm('reason', e.target.value)} rows={3} placeholder={isDE ? 'Beschreiben Sie Ihre Motivation...' : 'Describe your motivation...'} style={{ ...inputStyle, resize: 'vertical' as const, fontFamily: 'inherit', minHeight: '80px' }} />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{isDE ? 'Erfahrung im Immobilienbereich' : 'Real Estate Experience'}</label>
          <textarea value={formData.experience} onChange={e => updateForm('experience', e.target.value)} rows={3} placeholder={isDE ? 'Beschreiben Sie Ihre Erfahrung...' : 'Describe your experience...'} style={{ ...inputStyle, resize: 'vertical' as const, fontFamily: 'inherit', minHeight: '80px' }} />
        </div>

        <button type="submit" disabled={isSubmitting} style={{ width: '100%', padding: '14px', background: '#D6B160', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', opacity: isSubmitting ? 0.7 : 1, marginTop: '8px' }}>
          {isSubmitting ? (isDE ? 'Wird gesendet...' : 'Submitting...') : (isDE ? 'Antrag absenden' : 'Submit Application')}
        </button>
      </form>
    </div>
  );
};

/* ================================================================
   DASHBOARD TAB — with bar chart & All/Year filter
   ================================================================ */
interface DashboardProps { isCooperationPartner: boolean; dashboardData: any; selectedYear: number | 'all'; setSelectedYear: (y: number | 'all') => void; isDE: boolean; }

const DashboardTab: React.FC<DashboardProps> = ({ isCooperationPartner, dashboardData, selectedYear, setSelectedYear, isDE }) => {
  const { t } = useLanguage();
  if (!isCooperationPartner) {
    return (
      <div style={{ background: 'white', borderRadius: '16px', padding: '40px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔒</div>
        <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '8px' }}>{isDE ? 'Dashboard nur für Partner' : 'Dashboard for Partners Only'}</h3>
        <p style={{ fontSize: '13px', color: '#666' }}>{isDE ? 'Bewerben Sie sich als Kooperationspartner.' : 'Apply as a Cooperation Partner first.'}</p>
      </div>
    );
  }

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const statusColors: any = { pending: '#f59e0b', processing: '#6366f1', paid: '#22c55e' };
  const statusLabels: any = { pending: isDE ? 'Ausstehend' : 'Pending', processing: isDE ? 'In Bearbeitung' : 'Processing', paid: isDE ? 'Bezahlt' : 'Paid' };
  const filterOptions: (number | 'all')[] = ['all', 2025, 2026, 2027];

  // Compute filtered totals
  const yearlySummary = dashboardData?.yearlySummary || [];
  const monthlyDetail = dashboardData?.monthlyDetail || [];

  let filteredTotal = 0;
  let filteredPaid = 0;
  let filteredPending = 0;

  if (selectedYear === 'all') {
    filteredTotal = dashboardData?.totalAllTime || 0;
    filteredPaid = dashboardData?.totalPaid || 0;
    filteredPending = dashboardData?.totalPending || 0;
  } else {
    const yearData = yearlySummary.find((y: any) => y.year === selectedYear);
    filteredTotal = yearData?.total || 0;
    filteredPaid = yearData?.paid || 0;
    filteredPending = yearData?.pending || 0;
  }

  // Build bar chart data
  let chartBars: { label: string; total: number; paid: number; pending: number }[] = [];

  if (selectedYear === 'all') {
    // Yearly bars
    chartBars = yearlySummary.map((yr: any) => ({
      label: String(yr.year),
      total: yr.total || 0,
      paid: yr.paid || 0,
      pending: (yr.total || 0) - (yr.paid || 0),
    })).sort((a: any, b: any) => parseInt(a.label) - parseInt(b.label));
  } else {
    // Monthly bars for selected year
    const monthMap: any = {};
    monthlyDetail.forEach((sheet: any) => {
      const m = sheet.month || 1;
      if (!monthMap[m]) monthMap[m] = { total: 0, paid: 0, pending: 0 };
      monthMap[m].total += sheet.totalCommission || 0;
      if (sheet.status === 'paid') monthMap[m].paid += sheet.totalCommission || 0;
      else monthMap[m].pending += sheet.totalCommission || 0;
    });
    // Show all 12 months
    for (let m = 1; m <= 12; m++) {
      chartBars.push({
        label: months[m - 1],
        total: monthMap[m]?.total || 0,
        paid: monthMap[m]?.paid || 0,
        pending: monthMap[m]?.pending || 0,
      });
    }
  }

  const maxBarValue = Math.max(...chartBars.map(b => b.total), 1);

  return (
    <div>
      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {filterOptions.map(opt => (
          <button key={String(opt)} onClick={() => setSelectedYear(opt)} style={{
            padding: '8px 18px', borderRadius: '20px', border: 'none',
            background: selectedYear === opt ? '#D6B160' : '#f5f5f5',
            color: selectedYear === opt ? 'white' : '#666',
            fontWeight: '600', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s'
          }}>
            {opt === 'all' ? (isDE ? 'Alle' : 'All') : opt}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>{isDE ? 'Gesamt' : 'Total Commission'}</div>
          <div style={{ fontSize: '16px', fontWeight: '700' }}>{filteredTotal.toLocaleString()}</div>
          <div style={{ fontSize: '10px', color: '#999' }}>AED</div>
        </div>
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderLeft: '3px solid #22c55e' }}>
          <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>{isDE ? 'Bezahlt' : 'Paid'}</div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#22c55e' }}>{filteredPaid.toLocaleString()}</div>
          <div style={{ fontSize: '10px', color: '#999' }}>AED</div>
        </div>
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderLeft: '3px solid #f59e0b' }}>
          <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>{isDE ? 'Ausstehend' : 'Pending'}</div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#f59e0b' }}>{filteredPending.toLocaleString()}</div>
          <div style={{ fontSize: '10px', color: '#999' }}>AED</div>
        </div>
      </div>

      {/* Bar Chart */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '16px' }}>
          <BarChart3 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{selectedYear === 'all'
            ? (isDE ? 'Jährliche Provisionen' : 'Yearly Commissions')
            : (isDE ? `Monatliche Provisionen ${selectedYear}` : `Monthly Commissions ${selectedYear}`)}
        </h3>
        {chartBars.some(b => b.total > 0) ? (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: selectedYear === 'all' ? '12px' : '4px', height: '180px', padding: '0 4px' }}>
            {chartBars.map((bar, idx) => {
              const barHeight = maxBarValue > 0 ? (bar.total / maxBarValue) * 150 : 0;
              const paidHeight = maxBarValue > 0 ? (bar.paid / maxBarValue) * 150 : 0;
              return (
                <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                  {/* Value label */}
                  {bar.total > 0 && (
                    <div style={{ fontSize: '9px', fontWeight: '600', color: '#333', marginBottom: '4px', whiteSpace: 'nowrap' }}>
                      {bar.total >= 1000 ? `${(bar.total / 1000).toFixed(0)}k` : bar.total.toLocaleString()}
                    </div>
                  )}
                  {/* Stacked bar */}
                  <div style={{
                    width: '100%',
                    maxWidth: selectedYear === 'all' ? '60px' : '28px',
                    height: `${Math.max(barHeight, bar.total > 0 ? 4 : 0)}px`,
                    borderRadius: '4px 4px 2px 2px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                  }}>
                    {/* Pending portion (top, gold) */}
                    {bar.pending > 0 && (
                      <div style={{
                        height: `${barHeight - paidHeight}px`,
                        background: 'linear-gradient(180deg, #D6B160, #c9a350)',
                        minHeight: bar.pending > 0 ? '2px' : '0'
                      }} />
                    )}
                    {/* Paid portion (bottom, green) */}
                    {bar.paid > 0 && (
                      <div style={{
                        height: `${paidHeight}px`,
                        background: 'linear-gradient(180deg, #22c55e, #16a34a)',
                        minHeight: bar.paid > 0 ? '2px' : '0'
                      }} />
                    )}
                    {/* Empty bar placeholder */}
                    {bar.total === 0 && (
                      <div style={{ height: '2px', background: '#eee', borderRadius: '2px' }} />
                    )}
                  </div>
                  {/* Label */}
                  <div style={{ fontSize: selectedYear === 'all' ? '11px' : '9px', color: '#888', marginTop: '6px', fontWeight: '500' }}>
                    {bar.label}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '30px', color: '#999' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div>
            <p style={{ fontSize: '13px' }}>{isDE ? 'Keine Daten vorhanden' : 'No data available'}</p>
          </div>
        )}
        {/* Legend */}
        {chartBars.some(b => b.total > 0) && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#666' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#22c55e' }} />
              {isDE ? 'Bezahlt' : 'Paid'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#666' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#D6B160' }} />
              {isDE ? 'Ausstehend' : 'Pending'}
            </div>
          </div>
        )}
      </div>

      {/* Yearly Summary Table — show when 'all' */}
      {selectedYear === 'all' && yearlySummary.length > 0 && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px' }}>📋 {isDE ? 'Jahresübersicht' : 'Yearly Summary'}</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead><tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                <th style={{ textAlign: 'left', padding: '8px 6px', color: '#666', fontWeight: '600' }}>{isDE ? 'Jahr' : 'Year'}</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', color: '#666', fontWeight: '600' }}>{isDE ? 'Gesamt' : 'Total'}</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', color: '#666', fontWeight: '600' }}>{isDE ? 'Bezahlt' : 'Paid'}</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', color: '#666', fontWeight: '600' }}>{isDE ? 'Offen' : 'Pending'}</th>
              </tr></thead>
              <tbody>{yearlySummary.map((yr: any) => (
                <tr key={yr.year} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ padding: '10px 6px', fontWeight: '600' }}>{yr.year}</td>
                  <td style={{ padding: '10px 6px', textAlign: 'right', fontWeight: '600' }}>AED {yr.total?.toLocaleString()}</td>
                  <td style={{ padding: '10px 6px', textAlign: 'right', color: '#22c55e' }}>AED {yr.paid?.toLocaleString()}</td>
                  <td style={{ padding: '10px 6px', textAlign: 'right', color: '#f59e0b' }}>AED {yr.pending?.toLocaleString()}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* Monthly Table — show when specific year */}
      {selectedYear !== 'all' && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px' }}>📅 {isDE ? `Monatliche Provisionen ${selectedYear}` : `Monthly Commissions ${selectedYear}`}</h3>
          {monthlyDetail.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead><tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                  <th style={{ textAlign: 'left', padding: '8px 6px', color: '#666', fontWeight: '600' }}>{isDE ? 'Monat' : 'Month'}</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px', color: '#666', fontWeight: '600' }}>{isDE ? 'Betrag' : 'Amount'}</th>
                  <th style={{ textAlign: 'center', padding: '8px 6px', color: '#666', fontWeight: '600' }}>{t('status')}</th>
                </tr></thead>
                <tbody>{monthlyDetail.map((sheet: any, idx: number) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f5f5f5' }}>
                    <td style={{ padding: '10px 6px' }}>{sheet.monthLabel || months[(sheet.month || 1) - 1]}</td>
                    <td style={{ padding: '10px 6px', textAlign: 'right', fontWeight: '600' }}>{sheet.currency || 'AED'} {(sheet.totalCommission || 0).toLocaleString()}</td>
                    <td style={{ padding: '10px 6px', textAlign: 'center' }}>
                      <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', color: statusColors[sheet.status] || '#666', background: `${statusColors[sheet.status] || '#666'}15` }}>{statusLabels[sheet.status] || sheet.status}</span>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '24px', color: '#999' }}><div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div><p style={{ fontSize: '13px' }}>{isDE ? 'Keine Provisionen für dieses Jahr' : 'No commissions for this year'}</p></div>
          )}
        </div>
      )}
    </div>
  );
};

/* ================================================================
   COMMISSIONS TAB — with filters, summary & in-app PDF viewer
   ================================================================ */
interface CommissionsProps { isCooperationPartner: boolean; commissionSheets: any[]; isDE: boolean; apiUrl: string; }

const CommissionsTab: React.FC<CommissionsProps> = ({ isCooperationPartner, commissionSheets, isDE, apiUrl }) => {
  const { t } = useLanguage();
  const [viewingFile, setViewingFile] = useState<{ url: string; title: string } | null>(null);
  const [filterYear, setFilterYear] = useState<number | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  if (!isCooperationPartner) {
    return (
      <div style={{ background: 'white', borderRadius: '16px', padding: '40px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔒</div>
        <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '8px' }}>{isDE ? 'Provisionen nur für Partner' : 'Commissions for Partners Only'}</h3>
        <p style={{ fontSize: '13px', color: '#666' }}>{isDE ? 'Bewerben Sie sich als Kooperationspartner.' : 'Apply as a Cooperation Partner first.'}</p>
      </div>
    );
  }

  const statusColors: any = { pending: '#f59e0b', processing: '#6366f1', paid: '#22c55e' };
  const statusIcons: any = { pending: <Clock size={12} />, processing: <RefreshCw size={12} />, paid: <CheckCircle size={12} /> };
  const statusLabels: any = { pending: isDE ? 'Ausstehend' : 'Pending', processing: isDE ? 'In Bearbeitung' : 'Processing', paid: isDE ? 'Bezahlt' : 'Paid' };

  // ---- Filters ----
  const availableYears = Array.from(new Set(commissionSheets.map((s: any) => s.year))).sort((a: number, b: number) => b - a);

  const filtered = commissionSheets.filter((s: any) => {
    if (filterYear !== 'all' && s.year !== filterYear) return false;
    if (filterStatus !== 'all' && s.status !== filterStatus) return false;
    return true;
  }).sort((a: any, b: any) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  });

  // ---- Summary (from filtered) ----
  const totalAmount = filtered.reduce((sum: number, s: any) => sum + (s.totalCommission || 0), 0);
  const paidAmount = filtered.filter((s: any) => s.status === 'paid').reduce((sum: number, s: any) => sum + (s.totalCommission || 0), 0);
  const pendingAmount = filtered.filter((s: any) => s.status !== 'paid').reduce((sum: number, s: any) => sum + (s.totalCommission || 0), 0);
  const paidCount = filtered.filter((s: any) => s.status === 'paid').length;
  const pendingCount = filtered.filter((s: any) => s.status === 'pending').length;
  const processingCount = filtered.filter((s: any) => s.status === 'processing').length;

  // ---- Google Drive proxy ----
  function getProxyUrl(fileUrl: string): string | null {
    if (!fileUrl) return null;
    const match = fileUrl.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (match && match[1]) return `${apiUrl}/api/cooperation/commission-file/${match[1]}`;
    return fileUrl;
  }

  function openFileViewer(fileUrl: string, title: string) {
    const proxyUrl = getProxyUrl(fileUrl);
    if (proxyUrl) setViewingFile({ url: proxyUrl, title });
  }

  const pillStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 14px', borderRadius: '20px', border: 'none',
    background: active ? '#1a1a1a' : '#f0f0f0',
    color: active ? '#D6B160' : '#666',
    fontWeight: '600', fontSize: '12px', cursor: 'pointer', transition: 'all 0.2s',
    whiteSpace: 'nowrap' as const,
  });

  return (
    <div>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        <div style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)', borderRadius: '14px', padding: '16px', color: 'white' }}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#D6B160', marginBottom: '6px', fontWeight: '600' }}>
            {isDE ? 'Gesamt' : 'Total Earned'}
          </div>
          <div style={{ fontSize: '20px', fontWeight: '700' }}>AED {totalAmount.toLocaleString()}</div>
          <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>{filtered.length} {isDE ? 'Abrechnungen' : 'statements'}</div>
        </div>
        <div style={{ background: 'white', borderRadius: '14px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#22c55e', fontWeight: '600' }}>
                {isDE ? 'Bezahlt' : 'Paid'}
              </div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#22c55e' }}>AED {paidAmount.toLocaleString()}</div>
            </div>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CheckCircle size={16} color="#22c55e" /></div>
          </div>
          <div style={{ height: '1px', background: '#f0f0f0', margin: '6px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#f59e0b', fontWeight: '600' }}>
                {isDE ? 'Offen' : 'Outstanding'}
              </div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#f59e0b' }}>AED {pendingAmount.toLocaleString()}</div>
            </div>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Clock size={16} color="#f59e0b" /></div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      {totalAmount > 0 && (
        <div style={{ background: 'white', borderRadius: '10px', padding: '12px 14px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#888', marginBottom: '6px' }}>
            <span>{isDE ? 'Auszahlungsfortschritt' : 'Payout Progress'}</span>
            <span style={{ fontWeight: '600', color: '#333' }}>{totalAmount > 0 ? Math.round((paidAmount / totalAmount) * 100) : 0}%</span>
          </div>
          <div style={{ height: '6px', background: '#f0f0f0', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0}%`, background: 'linear-gradient(90deg, #22c55e, #16a34a)', borderRadius: '3px', transition: 'width 0.5s ease' }} />
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ marginBottom: '16px' }}>
        {/* Year filter */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <button onClick={() => setFilterYear('all')} style={pillStyle(filterYear === 'all')}>
            {isDE ? 'Alle Jahre' : 'All Years'}
          </button>
          {availableYears.map((yr: number) => (
            <button key={yr} onClick={() => setFilterYear(yr)} style={pillStyle(filterYear === yr)}>
              {yr}
            </button>
          ))}
        </div>
        {/* Status filter */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button onClick={() => setFilterStatus('all')} style={pillStyle(filterStatus === 'all')}>
            {isDE ? 'Alle' : 'All'} ({commissionSheets.filter((s: any) => filterYear === 'all' || s.year === filterYear).length})
          </button>
          <button onClick={() => setFilterStatus('paid')} style={pillStyle(filterStatus === 'paid')}>
            <CheckCircle size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />{isDE ? 'Bezahlt' : 'Paid'} ({paidCount})
          </button>
          <button onClick={() => setFilterStatus('pending')} style={pillStyle(filterStatus === 'pending')}>
            <Clock size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />{isDE ? 'Ausstehend' : 'Pending'} ({pendingCount})
          </button>
          {processingCount > 0 && (
            <button onClick={() => setFilterStatus('processing')} style={pillStyle(filterStatus === 'processing')}>
              <RefreshCw size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />{isDE ? 'In Bearbeitung' : 'Processing'} ({processingCount})
            </button>
          )}
        </div>
      </div>

      {/* Commission List */}
      {filtered.length > 0 ? filtered.map((sheet: any, idx: number) => {
        const createdDate = sheet.createdAt?._seconds
          ? new Date(sheet.createdAt._seconds * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          : '';

        return (
          <div key={idx} style={{
            background: 'white', borderRadius: '14px', padding: '0', overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '12px',
            border: sheet.status === 'paid' ? '1px solid #dcfce7' : '1px solid #f5f5f5',
          }}>
            {/* Card header band */}
            <div style={{
              background: sheet.status === 'paid'
                ? 'linear-gradient(135deg, #f0fdf4, #dcfce7)'
                : sheet.status === 'processing'
                  ? 'linear-gradient(135deg, #eef2ff, #e0e7ff)'
                  : 'linear-gradient(135deg, #fffbeb, #fef3c7)',
              padding: '14px 16px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontWeight: '700', fontSize: '16px', color: '#1a1a1a' }}>{sheet.monthLabel || 'Commission'}</div>
                <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{createdDate}</div>
              </div>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                color: statusColors[sheet.status] || '#666',
                background: 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(4px)',
                border: `1px solid ${statusColors[sheet.status] || '#ddd'}30`,
              }}>
                {statusIcons[sheet.status]} {statusLabels[sheet.status] || sheet.status}
              </span>
            </div>

            {/* Card body */}
            <div style={{ padding: '14px 16px' }}>
              {/* Amount */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '12px', color: '#888', fontWeight: '500' }}>{isDE ? 'Provisionsbetrag' : 'Commission Amount'}</span>
                <span style={{ fontSize: '20px', fontWeight: '800', color: '#1a1a1a' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#999' }}>{sheet.currency || 'AED'} </span>
                  {(sheet.totalCommission || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>

              {/* Line items */}
              {sheet.items && sheet.items.length > 0 && (
                <div style={{ fontSize: '12px', color: '#555', padding: '10px 12px', background: '#fafafa', borderRadius: '10px', marginBottom: '10px', border: '1px solid #f0f0f0' }}>
                  {sheet.items.map((item: any, i: number) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: i < sheet.items.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                      <span style={{ color: '#555' }}>{item.description}</span>
                      <span style={{ fontWeight: '600', color: '#333' }}>{sheet.currency || 'AED'} {(item.amount || 0).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Notes */}
              {sheet.notes && (
                <div style={{ fontSize: '12px', color: '#777', marginBottom: '10px', padding: '8px 10px', background: '#f9f9f9', borderRadius: '8px', borderLeft: '3px solid #D6B160' }}>
                  📝 {sheet.notes}
                </div>
              )}

              {/* Payment reference */}
              {sheet.status === 'paid' && sheet.paymentReference && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#166534', marginBottom: '10px', padding: '6px 10px', background: '#f0fdf4', borderRadius: '8px' }}>
                  <span style={{ fontSize: '14px' }}>💳</span>
                  <span>{isDE ? 'Zahlungsreferenz' : 'Payment Ref'}: <strong>{sheet.paymentReference}</strong></span>
                </div>
              )}

              {/* Action button */}
              {sheet.fileUrl && (
                <button
                  onClick={() => openFileViewer(sheet.fileUrl, sheet.monthLabel || 'Commission Statement')}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    width: '100%', padding: '10px', marginTop: '4px',
                    background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                    borderRadius: '10px', color: '#D6B160', border: 'none',
                    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                    transition: 'opacity 0.2s',
                  }}
                >
                  <FileText size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{isDE ? 'Abrechnung ansehen' : 'View Statement'}
                </button>
              )}
            </div>
          </div>
        );
      }) : (
        <div style={{ textAlign: 'center', padding: '40px 24px', color: '#999', background: 'white', borderRadius: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
          <p style={{ fontSize: '14px', fontWeight: '600', color: '#555' }}>
            {filterYear !== 'all' || filterStatus !== 'all'
              ? (isDE ? 'Keine Ergebnisse für diesen Filter' : 'No results for this filter')
              : (isDE ? 'Noch keine Provisionsabrechnungen' : 'No commission statements yet')}
          </p>
          <p style={{ fontSize: '12px', marginTop: '4px' }}>
            {filterYear !== 'all' || filterStatus !== 'all'
              ? (isDE ? 'Versuchen Sie einen anderen Filter' : 'Try a different filter')
              : (isDE ? 'Ihre Abrechnungen werden hier angezeigt' : 'Your statements will appear here')}
          </p>
        </div>
      )}

      {/* In-App PDF Viewer Modal */}
      {viewingFile && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', zIndex: 9999,
          display: 'flex', flexDirection: 'column'
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', background: '#1a1a1a', color: 'white', flexShrink: 0
          }}>
            <div style={{ fontSize: '14px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: '12px' }}>
              <FileText size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{viewingFile.title}
            </div>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              <a href={viewingFile.url} download style={{ padding: '6px 12px', background: '#D6B160', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '12px', fontWeight: '600' }}>
                <Download size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{isDE ? 'Download' : 'Download'}
              </a>
              <button onClick={() => setViewingFile(null)} style={{ padding: '6px 12px', background: '#333', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer', fontWeight: '600' }}>
                ✕
              </button>
            </div>
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <iframe src={viewingFile.url} style={{ width: '100%', height: '100%', border: 'none' }} title={t('ptnCommissionStatement')} />
          </div>
        </div>
      )}
    </div>
  );
};

/* ================================================================
   REFERRAL PARTNER VIEW
   ================================================================ */
const ReferralPartnerView: React.FC<{ isDE: boolean }> = ({ isDE }) => (
  <div style={{ padding: '16px', maxWidth: '600px', margin: '0 auto' }}>
    <div style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)', borderRadius: '16px', padding: '24px', color: 'white', marginBottom: '20px' }}>
      <div style={{ fontSize: '12px', color: '#D6B160', fontWeight: '600', marginBottom: '4px', letterSpacing: '1px', textTransform: 'uppercase' }}>{isDE ? 'Empfehlungspartner' : 'Referral Partner'}</div>
      <h2 style={{ fontSize: '22px', fontWeight: '700', margin: '0 0 6px 0' }}>🔗 {isDE ? 'Empfehlungsprogramm' : 'Referral Program'}</h2>
      <p style={{ fontSize: '13px', opacity: 0.8, margin: 0 }}>{isDE ? 'Empfehlen Sie Freunde und verdienen Sie Provisionen' : 'Refer friends and earn commissions'}</p>
    </div>
    <div style={{ background: 'white', borderRadius: '16px', padding: '32px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' }}>
      <div style={{ fontSize: '56px', marginBottom: '16px' }}>🔗</div>
      <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>{isDE ? 'Demnächst verfügbar' : 'Coming Soon'}</h3>
      <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6', maxWidth: '360px', margin: '0 auto 20px' }}>{isDE ? 'Empfehlen Sie Freunde und Familie und verdienen Sie Provisionen für erfolgreiche Vermittlungen.' : 'Refer friends and family to Asset Desk and earn commissions for successful referrals.'}</p>
      <p style={{ fontSize: '13px', color: '#999' }}>{isDE ? 'Kontaktieren Sie Ihren Berater für Details.' : 'Contact your advisor for more details.'}</p>
    </div>
  </div>
);

export default PartnerView;